import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import {
  TrendingUp, TrendingDown, Users, DollarSign, Activity, Clock, Loader2,
  Calendar, ArrowRightLeft, BarChart3, Stethoscope, Settings2, X,
  GripVertical, Plus, TrendingUp as TrendingUpIcon,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────

const EN_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parseReportDate(dateStr: string): { day: number; month: number; year: number } | null {
  const parts = dateStr.split("/");
  if (parts.length === 2) {
    if (parts[0].length === 4) {
      // YYYY/MM
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      return { day: 1, month, year };
    }
    // DD/MM
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    return { day, month, year: new Date().getFullYear() };
  }
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    return { day, month, year };
  }
  return null;
}

function monthLabel(month: number, year: number): string {
  return `${EN_MONTHS[month - 1]} ${String(year).slice(-2)}`;
}

function currentMonthLabel(dateStr: string): string | null {
  const d = parseReportDate(dateStr);
  return d ? monthLabel(d.month, d.year) : null;
}

function prevMonthLabel(dateStr: string): string | null {
  const d = parseReportDate(dateStr);
  if (!d) return null;
  const month = d.month === 1 ? 12 : d.month - 1;
  const year = d.month === 1 ? d.year - 1 : d.year;
  return monthLabel(month, year);
}

function orderToYearMonth(order: number, baseYear = 2024): { year: number; month: number } {
  const idx = order - 1;
  return {
    year: baseYear + Math.floor(idx / 12),
    month: (idx % 12) + 1,
  };
}

function monthOrder(year: number, month: number, baseYear = 2024): number {
  return (year - baseYear) * 12 + month;
}

// ── Widgets / Config ───────────────────────────────────────────────────────

interface VersionInfo {
  version: string;
  upload_count: number;
  total_records: number;
  last_report_date: string;
  last_report_day?: number;
  last_upload: string;
}

interface KpiMetric {
  value: number;
  delta: number | null;
}

type SpecialtyOption = "All Specialties" | "Cardiology" | "Orthopedics" | "Pediatrics" | "Neurology" | "Oncology" | "Internal Medicine" | "Dermatology";
type ViewMode = "current" | "actuals";

interface WidgetConfig {
  id: string;
  type: "kpi" | "summary" | "chart" | "specialties" | "units";
  title?: string;
  metric?: string;
  sheet?: string;
  period_type?: "month" | "day" | "total";
  entity_level?: "specialty" | "unit" | "doctor" | "group";
  prefix?: string;
  suffix?: string;
  mode?: "sum" | "avg";
}

const SPECIALTIES: SpecialtyOption[] = ["All Specialties", "Cardiology", "Orthopedics", "Pediatrics", "Neurology", "Oncology", "Internal Medicine", "Dermatology"];
const ALL_DEMO_SPECIALTIES = ["Cardiology", "Orthopedics", "Pediatrics", "Neurology", "Oncology", "Internal Medicine", "Dermatology"];

const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: "builtin-kpis", type: "kpi" },
  { id: "builtin-chart", type: "chart" },
  { id: "builtin-summary", type: "summary" },
  { id: "builtin-specialties", type: "specialties" },
  { id: "builtin-units", type: "units" },
];

const KPI_DEFS = [
  { key: "Consultations N // Δ Budget", label: "Consultations", icon: Users, prefix: "", sheet: "Consultation" },
  { key: "Billing N // Δ Budget", label: "Billing", icon: DollarSign, prefix: "€", suffix: "k", sheet: "Billing by Specialty" },
  { key: "Occupancy Rate // Δ N-1", label: "Occupancy Rate", icon: Activity, prefix: "", suffix: "%", mode: "avg", sheet: "Consultation" },
  { key: "First Consultations N", label: "First Consultations", icon: Clock, prefix: "", sheet: "Consultation" },
];

const CHART_PALETTE = ["#005F71", "#217D84", "#3A9A9F", "#52B7BB", "#6BD4D7"];

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [specialty, setSpecialty] = useState<SpecialtyOption>("All Specialties");
  const [viewMode, setViewMode] = useState<ViewMode>("current");
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<Record<string, Record<string, KpiMetric>>>({});
  const [monthlyData, setMonthlyData] = useState<{ label: string; value: number; order: number }[]>([]);
  const [dailyData, setDailyData] = useState<{ day: string; value: number; order: number }[]>([]);
  const [unitMonthlyData, setUnitMonthlyData] = useState<{ name: string; monthly: Record<string, number> }[]>([]);
  const [specialtyMonthlyData, setSpecialtyMonthlyData] = useState<{ name: string; monthly: Record<string, number> }[]>([]);
  const [customizing, setCustomizing] = useState(false);
  const [widgets, setWidgets] = useState<WidgetConfig[]>(() => {
    try {
      const saved = JSON.parse(user?.dashboard_layout || "null");
      return Array.isArray(saved) && saved.length ? saved : DEFAULT_WIDGETS;
    } catch {
      return DEFAULT_WIDGETS;
    }
  });
  const [dataFilterOptions, setDataFilterOptions] = useState<{ sheets: string[]; metrics: string[] }>({ sheets: [], metrics: [] });
  const [newWidget, setNewWidget] = useState<Partial<WidgetConfig>>({ type: "kpi", period_type: "month", entity_level: "specialty" });

  const [dailyCutoffDay, setDailyCutoffDay] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem("dashboardDailyCutoff");
      return saved ? parseInt(saved, 10) : null;
    } catch {
      return null;
    }
  });

  const effectiveDailyCutoff = dailyCutoffDay ?? version?.last_report_day ?? 31;

  useEffect(() => {
    if (dailyCutoffDay != null) {
      localStorage.setItem("dashboardDailyCutoff", String(dailyCutoffDay));
    } else {
      localStorage.removeItem("dashboardDailyCutoff");
    }
  }, [dailyCutoffDay]);

  useEffect(() => {
    setLoading(true);
    fetch("/api/version")
      .then((r) => r.json())
      .then(setVersion)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeSpecs = specialty === "All Specialties" ? ALL_DEMO_SPECIALTIES : [specialty];

  const reportDate = version?.last_report_date || "-";
  const reportDateParts = parseReportDate(reportDate);
  const reportYear = reportDateParts?.year;
  const reportMonth = reportDateParts?.month;
  const currentMonthLabelValue = reportDate ? currentMonthLabel(reportDate) : null;
  const prevMonthLabelValue = reportDate ? prevMonthLabel(reportDate) : null;

  // KPIs
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        Promise.all(
          KPI_DEFS.map((def) =>
            fetch(
              `/api/data/timeseries?sheet=${encodeURIComponent(def.sheet)}&metric=${encodeURIComponent(def.key)}&period_type=month&entity_level=specialty&specialty=${spec}&unit=__none__`,
              { headers }
            ).then((r) => r.json())
          )
        ).then((results) => ({ spec, results }))
      )
    ).then((allResults) => {
      const byMetric: Record<string, Record<string, KpiMetric>> = {};
      KPI_DEFS.forEach((def, defIdx) => {
        byMetric[def.key] = {};
        allResults.forEach(({ results }) => {
          (results[defIdx] || []).forEach((d: any) => {
            if (d.value != null) {
              const existing = byMetric[def.key][d.period_label];
              const val = d.value_numeric ?? d.value;
              if (existing) {
                existing.value += val;
              } else {
                byMetric[def.key][d.period_label] = { value: val, delta: d.delta_pct };
              }
            }
          });
        });
      });
      setKpiData(byMetric);
    }).catch(() => setKpiData({}));
  }, [specialty]);

  // Monthly consultations trend
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=Consultation&metric=${encodeURIComponent("Consultations N // Δ Budget")}&period_type=month&entity_level=specialty&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const byPeriod: Record<string, { label: string; value: number; order: number }> = {};
      results.forEach((data) => {
        data.forEach((d: any) => {
          if (d.value != null) {
            if (!byPeriod[d.period_label]) {
              byPeriod[d.period_label] = { label: d.period_label, value: 0, order: d.period_order ?? 0 };
            }
            byPeriod[d.period_label].value += d.value;
          }
        });
      });
      setMonthlyData(Object.values(byPeriod).sort((a, b) => a.order - b.order));
    }).catch(() => setMonthlyData([]));
  }, [specialty]);

  // Daily consultations for current month: fetch at unit level and aggregate by day
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=Consultation&metric=${encodeURIComponent("Consultations N // Δ Budget")}&period_type=day&entity_level=unit&specialty=${spec}`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const byDay: Record<string, { day: string; value: number; order: number }> = {};
      results.forEach((data) => {
        data.forEach((d: any) => {
          if (d.value != null) {
            const day = d.period_label;
            if (!byDay[day]) {
              byDay[day] = { day, value: 0, order: parseInt(day) || 0 };
            }
            byDay[day].value += d.value;
          }
        });
      });
      setDailyData(Object.values(byDay).sort((a, b) => a.order - b.order));
    }).catch(() => setDailyData([]));
  }, [specialty]);

  // Unit monthly data for breakdown
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    fetch(
      `/api/data/timeseries?sheet=Consultation&metric=${encodeURIComponent("Consultations N // Δ Budget")}&period_type=month&entity_level=unit`,
      { headers }
    )
      .then((r) => r.json())
      .then((data) => {
        const byUnit: Record<string, { name: string; monthly: Record<string, number> }> = {};
        data.forEach((d: any) => {
          if (!byUnit[d.entity_name]) {
            byUnit[d.entity_name] = { name: d.entity_name, monthly: {} };
          }
          byUnit[d.entity_name].monthly[d.period_label] = d.value_numeric ?? d.value ?? 0;
        });
        setUnitMonthlyData(Object.values(byUnit));
      })
      .catch(() => setUnitMonthlyData([]));
  }, [specialty]);

  // Specialty monthly data for breakdown
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=Consultation&metric=${encodeURIComponent("Consultations N // Δ Budget")}&period_type=month&entity_level=specialty&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const bySpec: Record<string, { name: string; monthly: Record<string, number> }> = {};
      results.forEach((series) => {
        series.forEach((d: any) => {
          if (!bySpec[d.entity_name]) {
            bySpec[d.entity_name] = { name: d.entity_name, monthly: {} };
          }
          bySpec[d.entity_name].monthly[d.period_label] = d.value_numeric ?? d.value ?? 0;
        });
      });
      setSpecialtyMonthlyData(Object.values(bySpec));
    }).catch(() => setSpecialtyMonthlyData([]));
  }, [specialty]);

  useEffect(() => {
    api.getFilters().then((f) => setDataFilterOptions({ sheets: f.sheets || [], metrics: f.metrics || [] })).catch(() => {});
  }, []);

  const actualMonthLabels = (() => {
    if (!reportYear || !reportMonth) return [];
    const labels: string[] = [];
    for (let m = 1; m < reportMonth; m++) {
      labels.push(monthLabel(m, reportYear));
    }
    return labels;
  })();

  const currentMonthValue = currentMonthLabelValue
    ? monthlyData.find((d) => d.label === currentMonthLabelValue)?.value || 0
    : 0;

  const prevMonthValue = prevMonthLabelValue
    ? monthlyData.find((d) => d.label === prevMonthLabelValue)?.value || 0
    : 0;

  const actualsTotal = monthlyData
    .filter((d) => actualMonthLabels.includes(d.label))
    .reduce((sum, d) => sum + (d.value || 0), 0);

  const visibleUnitData = (() => {
    return unitMonthlyData
      .map((u) => ({
        name: u.name,
        value: viewMode === "current"
          ? (currentMonthLabelValue ? u.monthly[currentMonthLabelValue] || 0 : 0)
          : actualMonthLabels.reduce((sum, label) => sum + (u.monthly[label] || 0), 0),
      }))
      .filter((u) => u.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  })();

  const visibleSpecialtyData = (() => {
    const rows = specialtyMonthlyData.map((s) => ({
      name: s.name,
      value: viewMode === "current"
        ? (currentMonthLabelValue ? s.monthly[currentMonthLabelValue] || 0 : 0)
        : actualMonthLabels.reduce((sum, label) => sum + (s.monthly[label] || 0), 0),
    }));
    if (specialty === "All Specialties" && rows.length > 1) {
      const combined = rows.reduce((sum, r) => sum + r.value, 0);
      rows.push({ name: "Combined", value: combined });
    }
    return rows
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value);
  })();

  const actualsChartData = monthlyData.filter((d) => actualMonthLabels.includes(d.label));

  const filteredDailyData = dailyData.filter((d) => parseInt(d.day) <= effectiveDailyCutoff);

  const chartData = viewMode === "current" ? filteredDailyData : actualsChartData;

  const chartTotal = viewMode === "current"
    ? filteredDailyData.reduce((sum, d) => sum + (d.value || 0), 0)
    : actualsTotal;

  const chartXKey = viewMode === "current" ? "day" : "label";
  const chartTitle = viewMode === "current"
    ? `Daily consultations in ${currentMonthLabelValue || "current month"}`
    : `Actual months (${actualMonthLabels.join(", ")}) · total ${chartTotal.toLocaleString()}`;

  const saveWidgets = async (next: WidgetConfig[]) => {
    setWidgets(next);
    try {
      const updated = await api.updateMe({ dashboard_layout: JSON.stringify(next) });
      setUser(updated);
    } catch (e) {
      console.error("Failed to save dashboard layout", e);
    }
  };

  const removeWidget = (id: string) => {
    saveWidgets(widgets.filter((w) => w.id !== id));
  };

  const moveWidget = (id: string, direction: -1 | 1) => {
    const idx = widgets.findIndex((w) => w.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= widgets.length) return;
    const next = [...widgets];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    saveWidgets(next);
  };

  const addCustomWidget = () => {
    if (!newWidget.metric || !newWidget.sheet) return;
    const title = newWidget.title || newWidget.metric;
    const config: WidgetConfig = {
      id: `custom-${Date.now()}`,
      type: newWidget.type || "kpi",
      title,
      metric: newWidget.metric,
      sheet: newWidget.sheet,
      period_type: newWidget.period_type || "month",
      entity_level: newWidget.entity_level || "specialty",
      prefix: newWidget.prefix || "",
      suffix: newWidget.suffix || "",
      mode: newWidget.mode || "sum",
    };
    saveWidgets([...widgets, config]);
    setNewWidget({ type: "kpi", period_type: "month", entity_level: "specialty" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-novacare-700" />
      </div>
    );
  }

  const renderKpis = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {KPI_DEFS.map((card) => {
          const monthly = kpiData[card.key] || {};
          let value = 0;
          let delta: number | null = null;
          if (viewMode === "current") {
            const current = currentMonthLabelValue ? monthly[currentMonthLabelValue] : null;
            value = current?.value || 0;
            delta = current?.delta ?? null;
          } else {
            const actuals = Object.entries(monthly)
              .filter(([label]) => actualMonthLabels.includes(label))
              .map(([, v]) => v.value || 0);
            if (actuals.length === 0) {
              value = 0;
            } else if ((card as any).mode === "avg") {
              value = actuals.reduce((sum, v) => sum + v, 0) / actuals.length;
            } else {
              value = actuals.reduce((sum, v) => sum + v, 0);
            }
          }
          const isPositive = delta != null && delta >= 0;
          const displayValue =
            (card as any).mode === "avg"
              ? `${Math.round(value)}%`
              : `${card.prefix}${value.toLocaleString()}${card.suffix || ""}`;
          return (
            <div key={card.key} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-novacare-50">
                  <card.icon className="w-5 h-5 text-novacare-700" />
                </div>
                <span className="text-sm text-slate-500">{card.label}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-slate-900">{displayValue}</span>
                {delta != null && (
                  <span className={`flex items-center text-sm font-medium ${isPositive ? "text-green-600" : "text-red-500"}`}>
                    {isPositive ? <TrendingUp className="w-3.5 h-3.5 mr-0.5" /> : <TrendingDown className="w-3.5 h-3.5 mr-0.5" />}
                    {delta > 0 ? "+" : ""}{delta}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSummary = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {viewMode === "current" ? `Current Month (${currentMonthLabelValue || "-"})` : "Actuals (Jan–Jul)"}
          </p>
        </div>
        <p className="text-3xl font-bold text-slate-900">
          {(viewMode === "current" ? currentMonthValue : actualsTotal).toLocaleString()}
        </p>
        <p className="text-sm text-slate-500 mt-1">
          {viewMode === "current" ? "consultations · partial month up to report date" : "consultations · sum of Jan–Jul actual months"}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <ArrowRightLeft className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {viewMode === "current" ? "vs Previous Month" : "vs August"}
          </p>
        </div>
        <div className="flex items-baseline gap-2">
          <p className="text-3xl font-bold text-slate-900">
            {(viewMode === "current" ? prevMonthValue : currentMonthValue).toLocaleString()}
          </p>
          <ComparisonBadge viewMode={viewMode} currentValue={currentMonthValue} actualsTotal={actualsTotal} prevValue={prevMonthValue} />
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {viewMode === "current" ? `${prevMonthLabelValue || "-"} baseline` : `${currentMonthLabelValue || "-"} report month`}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {viewMode === "current" ? "Current Month Avg / Day" : "Jan–Jul Average"}
          </p>
        </div>
        <p className="text-3xl font-bold text-slate-900">
          {viewMode === "current"
            ? Math.round(chartData.length ? chartTotal / chartData.length : 0).toLocaleString()
            : Math.round(actualMonthLabels.length ? actualsTotal / actualMonthLabels.length : 0).toLocaleString()}
        </p>
        <p className="text-sm text-slate-500 mt-1">
          {viewMode === "current" ? "avg consultations / day" : "avg consultations / month"}
        </p>
      </div>
    </div>
  );

  const renderChart = () => (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Consultations by {viewMode === "current" ? "Day" : "Month"} ({specialty === "All Specialties" ? "All Specialties" : specialty})
          </h3>
          <p className="text-xs text-slate-500">{chartTitle}</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full border ${
          viewMode === "current" ? "bg-novacare-50 border-novacare-200 text-novacare-700" : "bg-green-50 border-green-200 text-green-700"
        }`}>
          {viewMode === "current" ? currentMonthLabelValue || "Current" : "Actuals"}
        </span>
      </div>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey={chartXKey} stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
              formatter={(value: any) => [Number(value).toLocaleString(), "Consultations"]}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#005F71" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-sm text-slate-400 py-8 text-center">No chart data available.</p>
      )}
    </div>
  );

  const renderSpecialties = () => {
    const maxValue = Math.max(...visibleSpecialtyData.map((s) => s.value || 0), 1);
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-slate-400" />
          Consultations by Specialty ({viewMode === "current" ? currentMonthLabelValue || "August" : "Jan–Jul"})
        </h3>
        {visibleSpecialtyData.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">No specialty data available.</p>
        ) : (
          <div className="space-y-3">
            {visibleSpecialtyData.map((s) => (
              <div key={s.name} className="flex items-center justify-between">
                <span className={`text-sm ${s.name === "Combined" ? "font-semibold text-novacare-800" : "text-slate-700"}`}>
                  {s.name}
                  {s.name === "Combined" && <span className="ml-1 text-xs font-normal text-slate-500">(All)</span>}
                </span>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${s.name === "Combined" ? "bg-novacare-800" : "bg-novacare-600"}`}
                      style={{ width: `${Math.min(100, (s.value / maxValue) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-slate-900 w-16 text-right">{s.value.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderUnits = () => (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-slate-400" />
        Top Health Units ({viewMode === "current" ? currentMonthLabelValue || "August" : "Jan–Jul"})
      </h3>
      <ResponsiveContainer width="100%" height={Math.max(280, visibleUnitData.length * 28)}>
        <BarChart data={visibleUnitData} layout="vertical" margin={{ left: 100 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis type="number" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={100} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
          <Bar dataKey="value" fill="#217D84" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderWidget = (widget: WidgetConfig, index: number) => {
    if (widget.type === "kpi" && widget.id === "builtin-kpis") return renderKpis();
    if (widget.type === "summary" && widget.id === "builtin-summary") return renderSummary();
    if (widget.type === "chart" && widget.id === "builtin-chart") return renderChart();
    if (widget.type === "specialties" && widget.id === "builtin-specialties") return renderSpecialties();
    if (widget.type === "units" && widget.id === "builtin-units") return renderUnits();
    return <CustomWidget key={widget.id} config={widget} specialty={specialty} viewMode={viewMode} reportMonth={reportMonth} onRemove={() => removeWidget(widget.id)} />;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">
            NovaCare Health · {specialty === "All Specialties" ? "All Specialties" : specialty} · Report: {reportDate}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <button
            onClick={() => setCustomizing(!customizing)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:border-novacare-400 transition-colors"
          >
            <Settings2 className="w-4 h-4" />
            Customize
          </button>
          <button
            onClick={async () => {
              const html = await api.getDirectorBriefHtml(
                specialty === "All Specialties" ? "" : specialty,
                specialty !== "All Specialties" && specialty !== "Dermatology"
              );
              const blob = new Blob([html], { type: "text/html" });
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank");
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-novacare-700 rounded-lg hover:bg-novacare-800 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            Director Brief
          </button>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode("current")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === "current" ? "bg-novacare-700 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Current
            </button>
            <button
              onClick={() => setViewMode("actuals")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === "actuals" ? "bg-novacare-700 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Actuals (Jan–Jul)
            </button>
          </div>
          {viewMode === "current" && (
            <div className="flex items-center gap-2">
              <select
                value={effectiveDailyCutoff}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setDailyCutoffDay(val === (version?.last_report_day ?? 31) ? null : val);
                }}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-novacare-700"
                title="Daily chart cutoff"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>Days 1–{d}</option>
                ))}
              </select>
            </div>
          )}
          <select
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value as SpecialtyOption)}
            className="border border-slate-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-novacare-700"
          >
            {SPECIALTIES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {customizing && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Customize Dashboard</h3>
            <button onClick={() => setCustomizing(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-3">Reorder, remove or add data widgets below.</p>

          <div className="space-y-2 mb-4">
            {widgets.map((w, idx) => (
              <div key={w.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <GripVertical className="w-4 h-4 text-slate-300" />
                  <span className="text-sm text-slate-700">{w.title || w.type}</span>
                  {w.metric && <span className="text-xs text-slate-400">{w.sheet} · {w.metric}</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button disabled={idx <= 0} onClick={() => moveWidget(w.id, -1)} className="p-1 text-slate-400 hover:text-novacare-700 disabled:opacity-30">↑</button>
                  <button disabled={idx >= widgets.length - 1} onClick={() => moveWidget(w.id, 1)} className="p-1 text-slate-400 hover:text-novacare-700 disabled:opacity-30">↓</button>
                  <button onClick={() => removeWidget(w.id)} className="ml-2 p-1 text-red-400 hover:text-red-600" title="Remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add new widget
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <input
                type="text"
                placeholder="Widget title"
                value={newWidget.title || ""}
                onChange={(e) => setNewWidget({ ...newWidget, title: e.target.value })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
              <select
                value={newWidget.type}
                onChange={(e) => setNewWidget({ ...newWidget, type: e.target.value as any })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="kpi">Stat card</option>
                <option value="chart">Bar chart</option>
              </select>
              <select
                value={newWidget.sheet || ""}
                onChange={(e) => setNewWidget({ ...newWidget, sheet: e.target.value })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select sheet…</option>
                {dataFilterOptions.sheets.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={newWidget.metric || ""}
                onChange={(e) => setNewWidget({ ...newWidget, metric: e.target.value })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select metric…</option>
                {dataFilterOptions.metrics.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <select
                value={newWidget.period_type || ""}
                onChange={(e) => setNewWidget({ ...newWidget, period_type: e.target.value as any })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="month">Monthly</option>
                <option value="day">Daily</option>
                <option value="total">Total</option>
              </select>
              <select
                value={newWidget.entity_level || ""}
                onChange={(e) => setNewWidget({ ...newWidget, entity_level: e.target.value as any })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="specialty">Specialty</option>
                <option value="unit">Unit</option>
                <option value="doctor">Doctor</option>
                <option value="group">Group</option>
              </select>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Prefix e.g. €"
                  value={newWidget.prefix || ""}
                  onChange={(e) => setNewWidget({ ...newWidget, prefix: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Suffix e.g. k"
                  value={newWidget.suffix || ""}
                  onChange={(e) => setNewWidget({ ...newWidget, suffix: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <button
              onClick={addCustomWidget}
              disabled={!newWidget.metric || !newWidget.sheet}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-novacare-700 rounded-lg hover:bg-novacare-800 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Add widget
            </button>
          </div>
        </div>
      )}

      {viewMode === "current" && reportMonth && (
        <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Showing the report month ({reportDate}) with daily breakdown up to day {effectiveDailyCutoff}. Use the dropdown to adjust the range.
        </div>
      )}

      {widgets.map((widget, idx) => (
        <div key={widget.id} className={`relative group ${widget.type === "specialties" || widget.type === "units" ? "mb-6" : ""}`}>
          {customizing && (
            <div className="absolute -top-2 -right-2 z-10 flex gap-1">
              <button onClick={() => moveWidget(widget.id, -1)} disabled={idx <= 0} className="p-1 bg-white border rounded shadow text-xs disabled:opacity-30">↑</button>
              <button onClick={() => moveWidget(widget.id, 1)} disabled={idx >= widgets.length - 1} className="p-1 bg-white border rounded shadow text-xs disabled:opacity-30">↓</button>
              <button onClick={() => removeWidget(widget.id)} className="p-1 bg-white border rounded shadow text-xs text-red-600">×</button>
            </div>
          )}
          {renderWidget(widget, idx)}
        </div>
      ))}
    </div>
  );
}

function CustomWidget({ config, specialty, viewMode, reportMonth, onRemove }: any) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const activeSpecs = specialty === "All Specialties" ? ALL_DEMO_SPECIALTIES : [specialty];

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    setLoading(true);

    const periodType = viewMode === "current" && config.period_type === "month" ? "day" : config.period_type;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=${encodeURIComponent(config.sheet)}&metric=${encodeURIComponent(config.metric)}&period_type=${periodType}&entity_level=${config.entity_level || "specialty"}&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const merged: Record<string, any> = {};
      results.forEach((series) => {
        series.forEach((d: any) => {
          const label = d.period_label;
          if (!merged[label]) {
            merged[label] = { label, value: 0, order: d.period_order ?? 0 };
          }
          merged[label].value += d.value_numeric ?? d.value ?? 0;
        });
      });
      setData(Object.values(merged).sort((a: any, b: any) => a.order - b.order));
      setLoading(false);
    }).catch(() => { setData([]); setLoading(false); });
  }, [config, specialty, viewMode, reportMonth]);

  const value = data.reduce((sum, d) => sum + (d.value || 0), 0);
  const display = `${config.prefix || ""}${value.toLocaleString()}${config.suffix || ""}`;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-novacare-700" />
      </div>
    );
  }

  if (config.type === "kpi") {
    const Icon = config.metric?.toLowerCase().includes("bill") ? DollarSign : config.metric?.toLowerCase().includes("ocup") ? Activity : config.metric?.toLowerCase().includes("cons") ? Users : TrendingUpIcon;
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6 hover:shadow-sm transition-shadow">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg bg-novacare-50"><Icon className="w-5 h-5 text-novacare-700" /></div>
          <span className="text-sm text-slate-500">{config.title}</span>
        </div>
        <p className="text-2xl font-bold text-slate-900">{display}</p>
      </div>
    );
  }

  const chartHeight = config.type === "chart" ? 300 : 280;
  const isHorizontal = config.entity_level === "unit" || config.entity_level === "doctor";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900">{config.title}</h3>
        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{config.period_type}</span>
      </div>
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={data} layout={isHorizontal ? "vertical" : "horizontal"} margin={isHorizontal ? { left: 100 } : undefined}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={!isHorizontal} vertical={isHorizontal} />
            <XAxis type={isHorizontal ? "number" : "category"} dataKey={isHorizontal ? undefined : "label"} stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis type={isHorizontal ? "category" : "number"} dataKey={isHorizontal ? "label" : undefined} stroke="#64748b" fontSize={11} width={isHorizontal ? 100 : undefined} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} formatter={(v: any) => [`${config.prefix || ""}${Number(v).toLocaleString()}${config.suffix || ""}`, config.title]} />
            <Bar dataKey="value" fill={CHART_PALETTE[Math.floor(Math.random() * CHART_PALETTE.length)]} radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-sm text-slate-400 py-8 text-center">No data available for this widget.</p>
      )}
    </div>
  );
}

function ComparisonBadge({ viewMode, currentValue, actualsTotal, prevValue }: any) {
  const primary = viewMode === "current" ? currentValue : actualsTotal;
  const compare = viewMode === "current" ? prevValue : currentValue;
  if (primary === 0 || compare === 0) return null;
  const pct = Math.abs(Math.round(((primary - compare) / compare) * 100));
  return (
    <span className={`text-sm font-medium ${primary >= compare ? "text-green-600" : "text-red-500"}`}>
      {primary >= compare ? "↑" : "↓"} {pct}%
    </span>
  );
}
